export class VisitWidgetModule {
    initialize() {
        this.blocks = document.querySelectorAll('.visit-widget-block');
        this.blocks.forEach(block => {
            if (block.dataset.visitWidgetInitialized === '1') return;
            block.dataset.visitWidgetInitialized = '1';
            new VisitWidget(block).initialize();
        });
    }
}

class VisitWidget {
    constructor(container) {
        this.container = container;
        this.surveyId = container.dataset.surveyId;
        this.wrapper = container.querySelector('.visit-widget-block__wrapper');
        this.heightRaf = null;
        this.mutationObserver = null;
        this.resizeObserver = null;
        this.mounted = false;
    }

    initialize() {
        if (!this.surveyId || !this.wrapper) return;
        
        this.createScript();
        // Try again shortly in case UTM module loads after init
        setTimeout(() => this.createScript(), 100);
        document.addEventListener('DOMContentLoaded', () => this.createScript());
        document.addEventListener('non-functional-cookies-accepted', () => setTimeout(() => this.createScript(), 100));

        // Start observing the rendered widget and adjust height dynamically
        this.observeWidget();

        // Also recalc on window resize
        window.addEventListener('resize', this.updateHeight.bind(this));
    }

    createScript() {
        if (this.mounted) return;
        if (!this.wrapper) return;

        const existingScript = this.wrapper.querySelector('script');
        if (existingScript) {
            this.mounted = true;
            return;
        }

        let scriptUrl = 'https://register.visitcloud.com/widget.js';
        let utmParams = {};

        // Try to get UTM parameters from the global UTM module
        if (window.utmModule && typeof window.utmModule.getUTMParameters === 'function') {
            try {
                utmParams = window.utmModule.getUTMParameters() || {};
            } catch (e) {
                console.warn('VisitWidget: failed to get UTM params from module', e);
            }
        }

        // Fallback: get UTM parameters from current URL
        if (Object.keys(utmParams).length === 0) {
            try {
                const urlParams = new URLSearchParams(window.location.search);
                const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
                utmKeys.forEach(key => {
                    const value = urlParams.get(key);
                    if (value) utmParams[key] = value;
                });
            } catch (e) {
                console.warn("VisitWidget: couldn't get UTM params from url", e);
            }
        }

        // Add UTM parameters to script URL
        if (Object.keys(utmParams).length > 0) {
            const url = new URL(scriptUrl);
            Object.entries(utmParams).forEach(([key, value]) => {
                if (value) url.searchParams.set(key, value);
            });
            scriptUrl = url.toString();
        }

        const script = document.createElement('script');
        script.src = scriptUrl;
        script.setAttribute('data-survey-id', this.surveyId);
        script.setAttribute('data-skip-welcome-page', 'true');
        this.wrapper.appendChild(script);
        this.mounted = true;
    }

    updateHeight() {
        if (!this.wrapper) return;

        const measure = () => {
            const prevHeight = this.wrapper.style.height;
            this.wrapper.style.height = 'auto';
            this.wrapper.style.minHeight = '0';

            let newHeight = this.wrapper.scrollHeight;
            if (!newHeight) {
                let max = 0;
                this.wrapper.querySelectorAll(':scope > *').forEach((el) => {
                    const rect = el.getBoundingClientRect();
                    const top = el.offsetTop || 0;
                    const h = top + rect.height;
                    if (h > max) max = h;
                });
                newHeight = max;
            }

            if (newHeight && (prevHeight !== `${newHeight}px`)) {
                this.wrapper.style.height = `${newHeight}px`;
                this.wrapper.style.overflow = 'hidden';
            }
        };

        if (this.heightRaf) cancelAnimationFrame(this.heightRaf);
        this.heightRaf = requestAnimationFrame(measure);
    }

    observeWidget() {
        if (!this.wrapper) return;

        // Observe DOM changes inside the wrapper (widget renders asynchronously)
        if (!this.mutationObserver) {
            this.mutationObserver = new MutationObserver(() => {
                this.updateHeight();
                this.attachResizeObserver();
            });

            this.mutationObserver.observe(this.wrapper, {
                childList: true,
                subtree: true,
            });
        }

        // Initial attempt to observe and size
        this.attachResizeObserver();
        this.updateHeight();
    }

    attachResizeObserver() {
        if (!this.wrapper) return;

        if (!this.resizeObserver) {
            this.resizeObserver = new ResizeObserver(() => {
                this.updateHeight();
            });
        }

        // Observe direct children which are likely to hold the widget UI
        const children = this.wrapper.children;
        for (let i = 0; i < children.length; i++) {
            const el = children[i];
            this.resizeObserver.observe(el);
        }
    }
}
